import * as Sentry from "@sentry/react";
import { Switch, Route, Redirect, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ErrorFallback } from "@/components/error-fallback";
import NotFound from "@/pages/not-found";

import AdminLoginPage from "@/pages/admin-login";
import AdminDashboard from "@/pages/admin-dashboard";
import AdminBuildings from "@/pages/admin-buildings";
import AdminApartments from "@/pages/admin-apartments";
import AdminContent from "@/pages/admin-content";
import AdminSubmissions from "@/pages/admin-submissions";
import AdminApartmentDetail from "@/pages/admin-apartment-detail";
import AdminPMProfile from "@/pages/admin-pm-profile";
import AdminCleanerProfile from "@/pages/admin-cleaner-profile";
import AdminCleaningJobs from "@/pages/admin-cleaning-jobs";
import AdminLaundryJobs from "@/pages/admin-laundry-jobs";
import AdminReservations from "@/pages/admin-reservations";
import AdminUsers from "@/pages/admin-users";
import AdminFinances from "@/pages/admin-finances";
import AdminReports from "@/pages/admin-reports";
import AdminBilling from "@/pages/admin-billing";
import AdminTickets from "@/pages/admin-tickets";
import AdminCaja from "@/pages/admin-caja";
import AdminInvoices from "@/pages/admin-invoices";
import AdminBooking from "@/pages/admin-booking";
import AdminChannels from "@/pages/admin-channels";
import AdminGuestPayments from "@/pages/admin-guest-payments";
import AdminUpsells from "@/pages/admin-upsells";
import AdminSmartLocks from "@/pages/admin-smart-locks";
import AdminCustomFields from "@/pages/admin-custom-fields";
import AdminCommissions from "@/pages/admin-commissions";
import AdminAffiliates from "@/pages/admin-affiliates";
import AdminMaintenance from "@/pages/admin-maintenance";
import AdminContractorInvoices from "@/pages/admin-contractor-invoices";
import AdminTaxCompliance from "@/pages/admin-tax-compliance";
import AdminForecast from "@/pages/admin-forecast";
import AdminLumi from "@/pages/admin-lumi";
import AdminCRM from "@/pages/admin-crm";
import AdminAutomation from "@/pages/admin-automation";
import AdminUnitCosts from "@/pages/admin-unit-costs";
import AdminInventory from "@/pages/admin-inventory";
import AdminCleaningInventory from "@/pages/admin-cleaning-inventory";
import AdminIntegrations from "@/pages/admin-integrations";
import AdminOrgSettings from "@/pages/admin-org-settings";
import AdminBillingSetup from "@/pages/admin-billing-setup";
import AdminDailyReports from "@/pages/admin-daily-reports";
import AdminContracts from "@/pages/admin-contracts";
import AdminAuditLogs from "@/pages/admin-audit-logs";
import AdminCrossOrg from "@/pages/admin-cross-org";
import AdminQaSettings from "@/pages/admin-qa-settings";
import SignContract from "@/pages/sign-contract";
import InvoicePay from "@/pages/invoice-pay";

import PlatformDashboard from "@/pages/platform-dashboard";
import OwnerDashboard from "@/pages/owner-dashboard";
import AffiliateDashboard from "@/pages/affiliate-dashboard";
import WorkerDashboard from "@/pages/worker-dashboard";

import HelpTutorial from "@/pages/help-tutorial";

import GuestHome from "@/pages/guest-home";
import GuestCheckin from "@/pages/guest-checkin";
import GuestCheckout from "@/pages/guest-checkout";
import GuestSection from "@/pages/guest-section";
import PublicBooking from "@/pages/public-booking";
import MarketplacePage from "@/pages/marketplace";
import MarketplaceAbout from "@/pages/marketplace-about";
import MarketplaceHosts from "@/pages/marketplace-hosts";
import LandingPage from "@/pages/landing";
import RegisterPage from "@/pages/register";
import CheckoutSuccessPage from "@/pages/checkout-success";
import PaymentRequired from "@/pages/payment-required";

import { useAuth } from "@/hooks/use-auth";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import OnboardingGuard from "@/components/onboarding-wizard";
import OrgOnboardingGuard from "@/components/org-onboarding-wizard";

function HomeRedirect() {
  const { user, isLoading } = useAuth();
  if (isLoading) return <div className="flex h-screen items-center justify-center">Loading...</div>;
  if (!user) return <LandingPage />;
  const role = (user as any).role;
  const impersonatingRole = (user as any).impersonatingRole;
  const effectiveRole = impersonatingRole || role;
  if (effectiveRole === "platform_admin") return <Redirect to="/platform" />;
  if (effectiveRole === "cleaner") return <Redirect to="/worker" />;
  if (effectiveRole === "maintenance") return <Redirect to="/worker" />;
  if (effectiveRole === "laundry") return <Redirect to="/worker" />;
  if (effectiveRole === "owner") return <Redirect to="/owner" />;
  if (effectiveRole === "affiliate") return <Redirect to="/affiliate" />;
  return <Redirect to="/admin" />;
}

function ImpersonationBanner() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [, setLocation] = useLocation();
  const impersonatingRole = (user as any)?.impersonatingRole;

  const stopMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/stop-impersonating");
      return res.json();
    },
    onSuccess: async () => {
      qc.clear();
      await qc.refetchQueries({ queryKey: ["/api/user"] });
      setLocation("/admin");
    },
  });

  if (!impersonatingRole) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-[9999] bg-amber-500 text-white text-center py-1.5 px-4 text-sm font-medium flex items-center justify-center gap-3" data-testid="banner-impersonation">
      <span>Viewing as {impersonatingRole} user</span>
      <button
        onClick={() => stopMutation.mutate()}
        className="bg-white text-amber-700 px-3 py-0.5 rounded text-xs font-bold hover:bg-amber-100 transition-colors"
        data-testid="button-stop-impersonating"
      >
        Back to Admin
      </button>
    </div>
  );
}

function ProtectedRoute({ component: Component, roles, featureKey }: { component: React.ComponentType; roles?: string[]; featureKey?: string }) {
  const { user, isLoading } = useAuth();
  if (isLoading) return <div className="flex h-screen items-center justify-center">Loading...</div>;
  if (!user) return <Redirect to="/auth" />;
  const role = (user as any).role;
  const impersonatingRole = (user as any).impersonatingRole;
  const effectiveRole = impersonatingRole || role;
  if (roles && !roles.includes(effectiveRole) && effectiveRole !== "super_admin") {
    if (effectiveRole === "platform_admin") return <Redirect to="/platform" />;
    if (effectiveRole === "cleaner") return <Redirect to="/cleaner" />;
    if (effectiveRole === "maintenance") return <Redirect to="/maintenance" />;
    if (effectiveRole === "owner") return <Redirect to="/owner" />;
    if (effectiveRole === "affiliate") return <Redirect to="/affiliate" />;
    return <Redirect to="/admin" />;
  }
  // Feature-level gate for staff users. super_admin bypasses disabledFeatures.
  if (featureKey && effectiveRole === "staff") {
    const disabled: string[] = ((user as any).disabledFeatures ?? []) as string[];
    if (disabled.includes(featureKey)) return <Redirect to="/admin" />;
  }
  // Block dashboard access until subscription checkout is completed
  const orgStatus = (user as any).orgStatus;
  const isAdminRole = effectiveRole === "super_admin" || effectiveRole === "staff";
  if (isAdminRole && !impersonatingRole && orgStatus === "trial") {
    return <Redirect to="/payment-required" />;
  }
  const skipOnboarding = effectiveRole === "platform_admin" || impersonatingRole;
  if ((effectiveRole === "super_admin" || effectiveRole === "staff") && !impersonatingRole) {
    return <OrgOnboardingGuard><Component /></OrgOnboardingGuard>;
  }
  if (skipOnboarding) return <Component />;
  return <OnboardingGuard><Component /></OnboardingGuard>;
}

function Router() {
  return (
    <Switch>
      <Route path="/">
        <HomeRedirect />
      </Route>
      <Route path="/auth" component={AdminLoginPage} />
      <Route path="/register" component={RegisterPage} />
      <Route path="/onboarding/success" component={CheckoutSuccessPage} />
      <Route path="/payment-required" component={PaymentRequired} />

      {/* Admin Protected Routes (super_admin only) */}
      <Route path="/admin">
        <ProtectedRoute component={AdminDashboard} roles={["super_admin", "staff"]} />
      </Route>
      <Route path="/admin/buildings">
        <ProtectedRoute component={AdminBuildings} roles={["super_admin", "staff"]} featureKey="buildings_units" />
      </Route>
      <Route path="/admin/pm/:userId">
        <ProtectedRoute component={AdminPMProfile} roles={["super_admin", "staff"]} />
      </Route>
      <Route path="/admin/cleaner/:userId">
        <ProtectedRoute component={AdminCleanerProfile} roles={["super_admin", "staff"]} />
      </Route>
      <Route path="/admin/apartments/:id">
        <ProtectedRoute component={AdminApartmentDetail} roles={["super_admin", "staff"]} featureKey="buildings_units" />
      </Route>
      <Route path="/admin/apartments">
        <ProtectedRoute component={AdminApartments} roles={["super_admin", "staff"]} featureKey="buildings_units" />
      </Route>
      <Route path="/admin/content">
        <ProtectedRoute component={AdminContent} roles={["super_admin", "staff"]} featureKey="content_guides" />
      </Route>
      <Route path="/admin/reservations">
        <ProtectedRoute component={AdminReservations} roles={["super_admin", "staff"]} featureKey="reservations" />
      </Route>
      <Route path="/admin/cleaning-jobs">
        <ProtectedRoute component={AdminCleaningJobs} roles={["super_admin", "staff"]} featureKey="cleaning_jobs" />
      </Route>
      <Route path="/admin/laundry-jobs">
        <ProtectedRoute component={AdminLaundryJobs} roles={["super_admin", "staff"]} />
      </Route>
      <Route path="/admin/maintenance">
        <ProtectedRoute component={AdminMaintenance} roles={["super_admin", "staff"]} featureKey="maintenance_ops" />
      </Route>
      <Route path="/admin/qa-settings">
        <ProtectedRoute component={AdminQaSettings} roles={["super_admin", "staff"]} featureKey="qa_vision" />
      </Route>
      <Route path="/admin/submissions">
        <ProtectedRoute component={AdminSubmissions} roles={["super_admin", "staff"]} featureKey="submissions_checkout" />
      </Route>
      <Route path="/admin/users">
        <ProtectedRoute component={AdminUsers} roles={["super_admin", "staff"]} featureKey="team_management" />
      </Route>
      <Route path="/admin/finances">
        <ProtectedRoute component={AdminFinances} roles={["super_admin", "staff"]} featureKey="finance_core" />
      </Route>
      <Route path="/admin/reports">
        <ProtectedRoute component={AdminReports} roles={["super_admin", "staff"]} featureKey="reports_basic" />
      </Route>
      <Route path="/admin/billing">
        <ProtectedRoute component={AdminBilling} roles={["super_admin", "staff"]} featureKey="billing_invoices" />
      </Route>
      <Route path="/admin/tickets">
        <ProtectedRoute component={AdminTickets} roles={["super_admin", "staff"]} featureKey="support_tickets" />
      </Route>
      <Route path="/admin/audit-logs">
        <ProtectedRoute component={AdminAuditLogs} roles={["super_admin", "staff"]} />
      </Route>
      <Route path="/admin/caja">
        <ProtectedRoute component={AdminCaja} roles={["super_admin", "staff"]} featureKey="cash_box" />
      </Route>
      <Route path="/admin/invoices">
        <ProtectedRoute component={AdminInvoices} roles={["super_admin", "staff"]} featureKey="billing_invoices" />
      </Route>
      <Route path="/admin/booking">
        <ProtectedRoute component={AdminBooking} roles={["super_admin", "staff"]} featureKey="booking_engine" />
      </Route>
      <Route path="/admin/channels">
        <ProtectedRoute component={AdminChannels} roles={["super_admin", "staff", "owner"]} featureKey="channel_manager" />
      </Route>
      <Route path="/admin/guest-payments">
        <ProtectedRoute component={AdminGuestPayments} roles={["super_admin", "staff", "owner"]} featureKey="guest_payments" />
      </Route>
      <Route path="/admin/upsells">
        <ProtectedRoute component={AdminUpsells} roles={["super_admin", "staff", "owner"]} featureKey="upsells_addons" />
      </Route>
      <Route path="/admin/smart-locks">
        <ProtectedRoute component={AdminSmartLocks} roles={["super_admin", "staff", "owner"]} featureKey="smart_locks" />
      </Route>
      <Route path="/admin/custom-fields">
        <ProtectedRoute component={AdminCustomFields} roles={["super_admin", "staff", "owner"]} featureKey="custom_fields" />
      </Route>
      <Route path="/admin/commissions">
        <ProtectedRoute component={AdminCommissions} roles={["super_admin", "staff"]} featureKey="commissions" />
      </Route>
      <Route path="/admin/affiliates">
        <ProtectedRoute component={AdminAffiliates} roles={["super_admin", "staff", "owner"]} featureKey="affiliates" />
      </Route>
      <Route path="/admin/contractor-invoices">
        <ProtectedRoute component={AdminContractorInvoices} roles={["super_admin", "staff"]} featureKey="contractor_invoices" />
      </Route>
      <Route path="/admin/tax-compliance">
        <ProtectedRoute component={AdminTaxCompliance} roles={["super_admin", "staff"]} featureKey="tax_compliance" />
      </Route>
      <Route path="/admin/forecast">
        <ProtectedRoute component={AdminForecast} roles={["super_admin", "staff"]} featureKey="forecast" />
      </Route>
      <Route path="/admin/lumi">
        <ProtectedRoute component={AdminLumi} roles={["super_admin", "staff"]} featureKey="lumi_ai" />
      </Route>
      <Route path="/admin/crm">
        <ProtectedRoute component={AdminCRM} roles={["super_admin", "staff"]} featureKey="guest_crm" />
      </Route>
      <Route path="/admin/automation">
        <ProtectedRoute component={AdminAutomation} roles={["super_admin", "staff"]} featureKey="automation_engine" />
      </Route>
      <Route path="/admin/unit-costs">
        <ProtectedRoute component={AdminUnitCosts} roles={["super_admin", "staff"]} featureKey="unit_costs" />
      </Route>
      <Route path="/admin/inventory">
        <ProtectedRoute component={AdminInventory} roles={["super_admin", "staff"]} featureKey="inventory" />
      </Route>
      <Route path="/admin/integrations">
        <ProtectedRoute component={AdminIntegrations} roles={["super_admin", "staff"]} featureKey="integrations_admin" />
      </Route>
      <Route path="/admin/org-settings">
        <ProtectedRoute component={AdminOrgSettings} roles={["super_admin", "staff"]} />
      </Route>
      <Route path="/admin/billing-setup">
        <ProtectedRoute component={AdminBillingSetup} roles={["super_admin"]} />
      </Route>
      <Route path="/admin/daily-reports">
        <ProtectedRoute component={AdminDailyReports} roles={["super_admin", "staff"]} featureKey="daily_reports" />
      </Route>
      <Route path="/admin/cleaning-inventory">
        <ProtectedRoute component={AdminCleaningInventory} roles={["super_admin", "staff"]} featureKey="cleaning_inventory" />
      </Route>
      <Route path="/admin/contracts">
        <ProtectedRoute component={AdminContracts} roles={["super_admin", "staff"]} />
      </Route>
      <Route path="/admin/cross-org">
        <ProtectedRoute component={AdminCrossOrg} roles={["super_admin", "staff"]} />
      </Route>

      {/* Public Contract Signing */}
      <Route path="/sign/:token" component={SignContract} />

      {/* Public Invoice Pay */}
      <Route path="/pay/:token" component={InvoicePay} />

      {/* Platform Admin Portal */}
      <Route path="/platform">
        <ProtectedRoute component={PlatformDashboard} roles={["platform_admin"]} />
      </Route>

      {/* Worker Portal — unified dashboard for operational roles */}
      <Route path="/worker">
        <ProtectedRoute component={WorkerDashboard} roles={["cleaner", "maintenance", "laundry", "staff", "super_admin"]} />
      </Route>

      {/* Legacy role portals — all redirect to WorkerDashboard */}
      <Route path="/cleaner">
        <ProtectedRoute component={WorkerDashboard} roles={["cleaner", "super_admin"]} />
      </Route>
      <Route path="/maintenance">
        <ProtectedRoute component={WorkerDashboard} roles={["maintenance", "super_admin"]} />
      </Route>
      <Route path="/laundry">
        <ProtectedRoute component={WorkerDashboard} roles={["laundry", "super_admin"]} />
      </Route>

      {/* Property Manager Portal */}
      <Route path="/owner">
        <ProtectedRoute component={OwnerDashboard} roles={["owner", "super_admin"]} />
      </Route>

      {/* Affiliate Portal */}
      <Route path="/affiliate">
        <ProtectedRoute component={AffiliateDashboard} roles={["affiliate", "super_admin"]} />
      </Route>

      {/* Help & Tutorial */}
      <Route path="/help">
        <ProtectedRoute component={HelpTutorial} roles={["platform_admin", "super_admin", "staff", "owner", "cleaner", "maintenance"]} />
      </Route>

      {/* Public Marketplace */}
      <Route path="/about" component={MarketplaceAbout} />
      <Route path="/for-hosts" component={MarketplaceHosts} />
      <Route path="/marketplace/:slug" component={MarketplacePage} />
      <Route path="/marketplace" component={MarketplacePage} />

      {/* Public Booking */}
      <Route path="/book/:orgSlug/:pageSlug" component={PublicBooking} />

      {/* Guest Routes */}
      <Route path="/g/:slug/section/:sectionId" component={GuestSection} />
      <Route path="/g/:slug/checkin" component={GuestCheckin} />
      <Route path="/g/:slug/checkout" component={GuestCheckout} />
      <Route path="/g/:slug" component={GuestHome} />

      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <ImpersonationBanner />
        <Sentry.ErrorBoundary fallback={<ErrorFallback />} showDialog={false}>
          <Router />
        </Sentry.ErrorBoundary>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
